import {Link, Navigate, Route, Routes} from 'react-router-dom'
import {Start} from './Start'
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LocaleContext, LoginContext, OverlayContext, ScrollContext } from '../common/App';
import { BurgerButtonBig, CloseButtonBig, HeaderBig, HeaderSmall, LangSelection, HeaderWithButton, Loading, ErrorComp, RadioCheckButton, SquareButton, DropdownInfo, NavigateSmart, SwitchButton } from '../common/Common';
import './WebsiteApp.css';
import { AboutUs } from './AboutUs';
import { Login } from '../common/Login';
import { Register } from './Register';
import { TwoFactorAuthLogin } from '../common/TwoFactorAuthLogin';
import { ForFamilies } from './ForFamilies';
import { sanitizeLocaleMessages, setLoginStatesFromJsonConsumer } from '../common/util';
import { ForMn } from './ForMn';
import { Diversity } from './Diversity';
import { Contact } from './Contact';
import { HowItWorks } from './HowItWorks';
import { ConsumerMenu } from '../ConsumerMenu';
import { LoadState, MediationStatus, UserType } from '../common/enums';
import { version } from 'dompurify';
import { ForgotPassword } from './ForgotPassword';
import { ResetPassword } from './ResetPassword';
import { TermsOfUse } from './TermsOfUse';
import { PrivacyPolicy } from './PrivacyPolicy';
import { Imprint } from './Imprint';
import { ConsumerContext } from '..';
import { PrivacyPolicyRegister } from './PrivacyPolicyRegister';
import { Faq } from './Faq';
import { PrivacyPolicyContact } from './PrivacyPolicyContact';
import { useNavigateSmart } from '../common/hooks';
import { FamOrNannyRegister } from './FamOrNannyRegister';
import { PrivacyPolicyCookies } from './PrivacyPolicyCookies';

export const WebsiteContext = createContext();

export function WebsiteApp(props) {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const overlay = useContext(OverlayContext);
    const scroll = useContext(ScrollContext);
    const consumer = useContext(ConsumerContext);

    const localeRef = useRef();

    // For Menu
    const menuRef = useRef(null);
    const menuBtnRef = useRef(null);
    const [menuVisible, setMenuVisible] = useState(false);

    const [loadState, setLoadState] = useState(LoadState.NONE);

    const navigate = useNavigateSmart();


    // FUNCTIONS:

    function handleMenuClick(e) {
        if(menuVisible) {
            setMenuVisible(false);
        }
        else {
            setMenuVisible(true);
        }
    }

    function handleLogin(json) {
        setLoginStatesFromJsonConsumer(json, consumer);

        if(login.navigateUrlAfterLogin.current) {
            navigate(login.navigateUrlAfterLogin.current);
            login.navigateUrlAfterLogin.current = null;
        }
        else if(!json.email_confirmed) {
            navigate("/confirm_mail");
        }
        else if(json.mediation_status === MediationStatus.NONE) {
            navigate("/collect_data/sensitive_data");
        }
        else if(json.mediation_status === MediationStatus.DATA_COLLECTED) {
            navigate("/be_patient");
        }
        else {
            navigate("/dashboard");
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        localeRef.current = locale.locale;
    }, [locale.locale]);

    useLayoutEffect(() => {
        setLoadState(LoadState.LOADING);
        overlay.incrLoadingCounter();

        async function loadLocaleData(localeStr) {
          const messages = await import(`./i18n/${localeStr}.json`);
          locale.setMsgs((curMsgs) => {
            return Object.assign(sanitizeLocaleMessages(messages), curMsgs);
          });
          setLoadState(LoadState.LOAD_SUCCESS);
          overlay.decrLoadingCounter();
        }

        loadLocaleData(localeRef.current);
    }, []);  // Only execute this initially as App does not render this component while loading a new locale

    useLayoutEffect(() => {
        function handleClickAnywhere(e) {
            if (menuRef.current && menuBtnRef.current && !menuRef.current.contains(e.target) && !menuBtnRef.current.contains(e.target)) {
                setMenuVisible(false);
            }
        }

        document.addEventListener("click", handleClickAnywhere);
        return () => document.removeEventListener("click", handleClickAnywhere);
    }, []);


    // RENDER:

    return(
        <WebsiteContext.Provider value={{}}>
            <div id="website_app">
                <div ref={menuRef}>
                    <ConsumerMenu menuVisible={menuVisible} onMenuClick={handleMenuClick} />
                </div>

                <HeaderWithButton onClick={handleMenuClick} menuBtnRef={menuBtnRef} />

                <Routes>
                    <Route exact path="/login" element={ <Login onLogin={handleLogin} /> } ></Route>
                    <Route exact path="/register" element={<FamOrNannyRegister/>}></Route>
                    <Route exact path="/register_mn" element={<Register onRegister={props.onRegister} userType={UserType.NANNY} />}></Route>
                    <Route exact path="/register_fam" element={<Register onRegister={props.onRegister} userType={UserType.FAMILY}/>}></Route>
                    <Route exact path="/two_factor_auth" element={<TwoFactorAuthLogin
                            onLogin={handleLogin}/>}
                    ></Route>

                    <Route path="/" element={ <Start />}></Route>
                    <Route path="/about_us" element={ <AboutUs />}></Route>
                    <Route path="/families" element={ <ForFamilies />}></Route>
                    <Route path="/m_n" element={ <ForMn />}></Route>
                    <Route path="/diversity" element={ <Diversity />}></Route>
                    <Route path="/contact" element={ <Contact />}></Route>
                    <Route path="/how_it_works" element={ <HowItWorks />}></Route>

                    <Route path="/forgot_password" element={ <ForgotPassword />}></Route>
                    <Route path="/reset_password" element={ <ResetPassword />}></Route>

                    <Route path="/terms_of_use" element={ <TermsOfUse />}></Route>
                    <Route path="/privacy_policy" element={ <PrivacyPolicy />}></Route>
                    <Route path="/privacy_policy_register" element={ <PrivacyPolicyRegister />}></Route>
                    <Route path="/privacy_policy_contact" element={ <PrivacyPolicyContact />}></Route>
                    <Route path="/privacy_policy_cookies" element={ <PrivacyPolicyCookies />}></Route>
                    <Route path="/imprint" element={ <Imprint />}></Route>
                    <Route path="/faq" element={ <Faq />}></Route>

                    <Route path="*" element={ <NavigateSmart to="/" />}></Route>
                </Routes>

            </div>
        </WebsiteContext.Provider>
    );
}
